/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useState } from "react";
import { meApi, formatApiError, api } from "@/lib/api";
import { PageHeader, Card, SaveBar } from "./_shared";
import { toast } from "sonner";
import { Printer, Loader2 } from "lucide-react";
import QuestionnaireConfirmationModal, { CompletedBadge } from "@/components/QuestionnaireConfirmationModal";

/**
 * Wedding preparation questionnaire — 10 sections, premium layout.
 * Mirrors the WordPress "Wedding Planner Premium" experience from
 * myanimation.fr, but styled to match the Emergent dashboard.
 */

// ------ Reusable field components ------
const eyebrow = "text-[10px] uppercase tracking-[0.22em] text-white/55 mb-1.5 block";
const inputCls =
  "w-full bg-white/[0.03] border border-white/10 focus:border-[#DCB457] px-3 py-2.5 text-sm text-white outline-none transition-colors";
const chipBase =
  "px-3.5 py-2 text-sm border transition-all cursor-pointer select-none";

function RadioGroup({ value, onChange, options, testid }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {options.map((opt) => {
        const active = value === opt;
        return (
          <button
            key={opt}
            type="button"
            onClick={() => onChange(opt)}
            className={`${chipBase} ${active ? "bg-[#DCB457]/15 border-[#DCB457] text-[#DCB457]" : "bg-white/[0.02] border-white/12 text-white/75 hover:border-[#DCB457]/50"}`}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

function Question({ label, hint, children }) {
  return (
    <div>
      <label className={eyebrow}>{label}</label>
      {hint && <p className="text-[11px] text-white/50 italic mb-2">{hint}</p>}
      {children}
    </div>
  );
}

function Section({ number, title, description, children }) {
  return (
    <div className="border border-white/10 bg-black/40 relative">
      <div className="flex items-baseline gap-3 border-b border-white/10 px-5 py-4">
        <span className="font-serif-luxe text-2xl text-[#DCB457] leading-none">{number}</span>
        <h2 className="font-serif-luxe text-lg text-white/95">{title}</h2>
      </div>
      {description && (
        <p className="px-5 pt-3 text-[12px] text-white/55 italic">{description}</p>
      )}
      <div className="p-5 space-y-5">{children}</div>
    </div>
  );
}

// ------ Choice sets ------
const CEREMONY = [
  "Oui, et le DJ doit se charger de la sonorisation",
  "Oui, mais nous passons par un maître de cérémonie",
  "Non, pas de cérémonie laïque prévue",
];
const ENTREE_SALLE = [
  "Haie d'honneur avec vos invités",
  "Avec vos témoins à vos côtés",
  "Entrée classique et élégante",
  "Entrée dansante et festive",
  "Autre",
];
const OUVERTURE_BAL = ["Restent assis à leur table", "Se lèvent et se placent en demi-cercle autour de la piste"];
const DJ_MICRO = ["Oui, avec beaucoup d'animation", "Oui, mais avec modération", "Non, nous préférons que le DJ soit discret"];
const JEUX = ["Oui, jeux classiques de mariage", "Non, nous préférons éviter les jeux"];
const QUI_ANIME = ["Le DJ", "Vos témoins ou amis"];
const AMBIANCE = ["Chic et élégante", "Détendue et festive", "Intime et romantique", "Dynamique et énergique"];
const OUI_NON = ["Oui", "Non"];
const EQUIPEMENT = ["Le lieu dispose de son propre équipement", "Le DJ doit fournir le système son"];
const DROIT_IMAGE = ["Oui, usage promotionnel", "Non, privé"];

export default function QuestionnairePrep() {
  const [d, setD] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [eventType, setEventType] = useState("");

  useEffect(() => {
    meApi.getQuestionnairePrep()
      .then((r) => {
        setD(r || {});
        if (r && (r.created_at || r.id)) setCompleted(true);
      })
      .catch(() => setD({}))
      .finally(() => setLoading(false));
    // Fetch user's event type (adapts the questionnaire wording & sections)
    meApi.summary().then((s) => {
      setEventType(((s && s.event_type) || "").toLowerCase());
    }).catch(() => {});
  }, []);

  const isMariage = !eventType || eventType.includes("mariage");
  const isAnniversaire = eventType.includes("anniv");
  const isEntreprise = eventType.includes("entrepris") || eventType.includes("professio") || eventType.includes("séminair") || eventType.includes("seminair");

  const set = (k) => (v) => setD((prev) => ({ ...prev, [k]: v }));

  const submit = async (e) => {
    e?.preventDefault?.();
    setSaving(true); setMsg(""); setErr("");
    try {
      const saved = await meApi.saveQuestionnairePrep(d);
      setD(saved || d);
      setCompleted(true);
      if (saved?.first_submit) {
        setShowConfirmation(true);
      } else {
        setMsg("Questionnaire enregistré.");
        toast.success("Questionnaire enregistré.");
      }
    } catch (e2) {
      const m = formatApiError(e2);
      setErr(m);
      toast.error(m);
    } finally {
      setSaving(false);
    }
  };

  const printPdf = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem("ma_token");
      const r = await fetch(`${api.defaults.baseURL}/me/questionnaire-prep/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error("Erreur PDF");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      // Open in a new tab so the browser's native print dialog is one click away
      const w = window.open(url, "_blank", "noopener,noreferrer");
      if (w) setTimeout(() => { try { w.print?.(); } catch { /* silent */ } }, 800);
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e2) {
      toast.error("Impossible de générer le PDF pour l'impression.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div data-testid="ec-questionnaire-prep">
      <QuestionnaireConfirmationModal
        open={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        isWedding={false}
      />

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <PageHeader
          eyebrow={isAnniversaire ? "Questionnaire anniversaire" : isEntreprise ? "Questionnaire événement d'entreprise" : "Wedding Planner Premium · Questionnaire prestige"}
          title={isAnniversaire ? "Préparez chaque moment clé de" : isEntreprise ? "Préparez chaque moment clé de" : "Préparez chaque moment clé de"}
          italic={isAnniversaire ? "votre anniversaire." : isEntreprise ? "votre événement." : "votre mariage."}
          desc="Renseignez les informations essentielles de votre événement — sections bien séparées, champs plus lisibles, enregistrement simplifié."
        />
        {completed && <div className="mt-4"><CompletedBadge label="Questionnaire complété" /></div>}
      </div>

      {/* Quick stat tiles (matching the source design) */}
      <div className="grid grid-cols-3 gap-3 mb-8">
        {[
          { k: "10", l: "SECTIONS", d: "Blocs organisés pour compléter chaque étape sereinement." },
          { k: "100%", l: "STYLE", d: "Interface visuellement alignée avec le reste du dashboard." },
          { k: "2", l: "ACTIONS", d: "Enregistrer ou imprimer votre questionnaire à tout moment." },
        ].map((t) => (
          <div key={t.l} className="card-luxe p-4">
            <p className="font-serif-luxe text-3xl text-[#DCB457]">{t.k}</p>
            <p className="text-[10px] uppercase tracking-[0.22em] text-white/55 mt-1">{t.l}</p>
            <p className="text-xs text-white/50 mt-1.5 leading-snug">{t.d}</p>
          </div>
        ))}
      </div>

      {loading ? (
        <Card>
          <div className="flex items-center gap-3 py-4 text-white/60">
            <Loader2 className="animate-spin" size={16} /> Chargement…
          </div>
        </Card>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          {/* 01 */}
          <Section number="01" title="Musiques et moments forts" description="Merci de compléter également la section Musiques dans l'onglet Ma Playlist.">
            {isMariage && (
              <>
                <Question label="Cérémonie laïque prévue ? *">
                  <RadioGroup value={d.ceremonie_laique} onChange={set("ceremonie_laique")} options={CEREMONY} />
                </Question>
                <Question label="Comment souhaitez-vous faire votre entrée dans la salle lors de la réception ? *"
                  hint="Que vous optiez pour le grand classique ou pour un twist festif, mettez le ton dès votre entrée.">
                  <RadioGroup value={d.entree_salle} onChange={set("entree_salle")} options={ENTREE_SALLE} />
                </Question>
                <Question label="Lors de l'ouverture de bal, préférez-vous que les convives :">
                  <RadioGroup value={d.ouverture_bal_position} onChange={set("ouverture_bal_position")} options={OUVERTURE_BAL} />
                </Question>
              </>
            )}
            {isAnniversaire && (
              <>
                <Question label={`Y a-t-il une personne à mettre à l'honneur ? *`}
                  hint="Ex : le/la fêté(e), un discours surprise, un moment mémorable…">
                  <textarea value={d.honneur_perso || ""} onChange={(e) => set("honneur_perso")(e.target.value)} rows={2} className={inputCls} placeholder="Ex : Sophie, 40 ans, discours de son frère à 21h…" />
                </Question>
                <Question label="Souhaitez-vous une chanson spéciale pour son entrée ou son arrivée ?">
                  <input type="text" value={d.chanson_entree_pref || ""} onChange={(e) => set("chanson_entree_pref")(e.target.value)} className={inputCls} placeholder="Titre — Artiste" />
                </Question>
                <Question label="Prévoyez-vous un moment gâteau/bougies avec musique ?">
                  <RadioGroup value={d.moment_gateau} onChange={set("moment_gateau")} options={OUI_NON} />
                </Question>
              </>
            )}
            {isEntreprise && (
              <>
                <Question label="Type d'événement d'entreprise *">
                  <input type="text" value={d.type_event_pro || ""} onChange={(e) => set("type_event_pro")(e.target.value)} className={inputCls} placeholder="Ex : Soirée fin d'année, lancement produit, séminaire…" />
                </Question>
                <Question label="Y a-t-il des prises de parole prévues (dirigeants, remise de prix…) ?">
                  <textarea value={d.prises_parole || ""} onChange={(e) => set("prises_parole")(e.target.value)} rows={2} className={inputCls} placeholder="Ex : discours PDG à 20h, remise trophées à 21h30…" />
                </Question>
              </>
            )}
          </Section>

          {/* 02 */}
          <Section number="02" title="Animation de la soirée">
            <Question label="Souhaitez-vous que le DJ anime avec un micro ? *">
              <RadioGroup value={d.dj_micro} onChange={set("dj_micro")} options={DJ_MICRO} />
            </Question>
            <Question label="Souhaitez-vous des jeux ou animations spécifiques pendant la soirée ? *"
              hint="Les jeux sont optionnels, mais les rires et les sourires ne le sont pas.">
              <RadioGroup value={d.jeux_animations} onChange={set("jeux_animations")} options={JEUX} />
            </Question>
            <Question label="Qui animera ces jeux ? *">
              <RadioGroup value={d.qui_anime_jeux} onChange={set("qui_anime_jeux")} options={QUI_ANIME} />
            </Question>
            <Question label="Idées d'animations">
              <textarea value={d.idees_animations || ""} onChange={(e) => set("idees_animations")(e.target.value)} rows={3} className={inputCls} placeholder="Décrivez vos souhaits en quelques mots…" />
            </Question>
            <Question label="Moments clés à annoncer">
              <textarea value={d.moments_a_annoncer || ""} onChange={(e) => set("moments_a_annoncer")(e.target.value)} rows={3} className={inputCls} placeholder="Ex : entrée, gâteau, discours, danse père-fille…" />
            </Question>
          </Section>

          {/* 03 */}
          <Section number="03" title="Ambiance et déroulement de la soirée">
            <Question label="Quelle ambiance générale souhaitez-vous pour votre soirée ? *">
              <RadioGroup value={d.ambiance_generale} onChange={set("ambiance_generale")} options={AMBIANCE} />
            </Question>
            <Question label="Faut-il prévoir un fond musical pendant les discours ? *">
              <RadioGroup value={d.musique_discours} onChange={set("musique_discours")} options={OUI_NON} />
            </Question>
            <Question label="Dress code ? *">
              <RadioGroup value={d.dress_code} onChange={set("dress_code")} options={OUI_NON} />
            </Question>
          </Section>

          {/* 04 */}
          <Section number="04" title="Logistique et organisation technique">
            <Question label="Le lieu dispose-t-il de son propre équipement sonore ? *">
              <RadioGroup value={d.equipement_lieu} onChange={set("equipement_lieu")} options={EQUIPEMENT} />
            </Question>
            <Question label="Restrictions techniques ou sonores">
              <textarea value={d.restrictions_techniques || ""} onChange={(e) => set("restrictions_techniques")(e.target.value)} rows={2} className={inputCls} placeholder="Ex : limite décibel, coupure son à 2h…" />
            </Question>
          </Section>

          {/* 05 */}
          <Section number="05" title="Droit à l'image">
            <Question label="Autorisez-vous les photos/vidéos ? *">
              <RadioGroup value={d.droit_image} onChange={set("droit_image")} options={DROIT_IMAGE} />
            </Question>
          </Section>

          {/* 06 */}
          <Section number="06" title="Sécurité et confort des invités">
            <Question label="Invités avec besoins spécifiques">
              <textarea value={d.invites_besoins_specifiques || ""} onChange={(e) => set("invites_besoins_specifiques")(e.target.value)} rows={2} className={inputCls} placeholder="Ex : PMR, allergies, enfants en bas âge…" />
            </Question>
          </Section>

          {/* 07 */}
          <Section number="07" title="Moments spéciaux">
            <Question label="Surprises prévues ? *">
              <RadioGroup value={d.surprises_prevues} onChange={set("surprises_prevues")} options={OUI_NON} />
            </Question>
            <Question label="Lancer de bouquet ? *">
              <RadioGroup value={d.lancer_bouquet} onChange={set("lancer_bouquet")} options={OUI_NON} />
            </Question>
          </Section>

          {/* 08 */}
          <Section number="08" title="Communication et contacts">
            <Question label="Contacts responsables">
              <textarea value={d.contacts_responsables || ""} onChange={(e) => set("contacts_responsables")(e.target.value)} rows={3} className={inputCls} placeholder="Nom + téléphone des personnes référentes le jour J" />
            </Question>
          </Section>

          {/* 09 */}
          <Section number="09" title="Prestataires">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Question label="Traiteur">
                <input value={d.traiteur || ""} onChange={(e) => set("traiteur")(e.target.value)} className={inputCls} placeholder="Nom + tél" />
              </Question>
              <Question label="Photographe">
                <input value={d.photographe || ""} onChange={(e) => set("photographe")(e.target.value)} className={inputCls} placeholder="Nom + tél" />
              </Question>
              <Question label="Wedding planner">
                <input value={d.wedding_planner || ""} onChange={(e) => set("wedding_planner")(e.target.value)} className={inputCls} placeholder="Nom + tél" />
              </Question>
            </div>
          </Section>

          {/* 10 */}
          <Section number="10" title="Autres précisions">
            <Question label="Informations complémentaires">
              <textarea value={d.infos_complementaires || ""} onChange={(e) => set("infos_complementaires")(e.target.value)} rows={4} className={inputCls} placeholder="Tout ce qui vous semble utile de nous partager…" />
            </Question>
          </Section>

          {/* Sticky footer with actions */}
          <div className="sticky bottom-0 mt-6 -mx-6 md:mx-0 bg-black/95 border-t border-[#DCB457]/25 px-5 py-4 flex items-center justify-between gap-3 flex-wrap">
            <div className="text-[11px] text-white/50 italic">
              Conseil : enregistrez vos réponses avant d'imprimer le questionnaire.
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={printPdf}
                disabled={downloading || saving}
                className="text-[11px] uppercase tracking-[0.2em] px-4 py-2.5 border border-[#DCB457]/50 text-[#DCB457] hover:bg-[#DCB457]/8 inline-flex items-center gap-2"
                data-testid="q-print-pdf"
              >
                {downloading ? <Loader2 size={12} className="animate-spin" /> : <Printer size={12} strokeWidth={1.6} />}
                Imprimer / PDF
              </button>
              <SaveBar saving={saving} message={msg || err} error={err} label="Enregistrer" />
            </div>
          </div>
        </form>
      )}
    </div>
  );
}
